"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { trackSearch, trackSearchResultClick } from "@/lib/gtag";

interface WordItem {
  slug: string;
  word: string;
  meanings: string[];
  level: string;
}

const DEBOUNCE_MS = 250;

/**
 * 首頁搜尋框：輸入後 debounce 250ms 打 /api/words?q=...，server 走 search-index 過濾。
 * 不再從 server prop 收 7,969 筆陣列，首頁 HTML 因此瘦回幾十 KB。
 */
export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WordItem[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 處理 race condition：req id 比對，舊 fetch 的回應會被丟棄
  const reqRef = useRef(0);

  const runSearch = useCallback(async (q: string) => {
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      const res = await fetch(`/api/words?q=${encodeURIComponent(q)}`);
      if (reqRef.current !== reqId) return;
      const data = await res.json();
      const list: WordItem[] = Array.isArray(data) ? data : [];
      setResults(list);
      if (q.length >= 2) trackSearch(q, list.length);
    } catch {
      if (reqRef.current === reqId) setResults([]);
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  }, []);

  const handleQueryChange = useCallback(
    (val: string) => {
      setQuery(val);
      const q = val.trim();

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!q) {
        // 取消進行中的 fetch、清空結果
        reqRef.current++;
        setResults([]);
        setLoading(false);
        return;
      }

      debounceRef.current = setTimeout(() => runSearch(q), DEBOUNCE_MS);
    },
    [runSearch],
  );

  const handleClear = useCallback(() => {
    setQuery("");
    setResults([]);
    setLoading(false);
    reqRef.current++;
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return (
    <div className="relative w-full">
      <div className="relative">
        <svg
          className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-amber-700"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
          />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="搜尋英文單字或中文意思..."
          className="w-full rounded-2xl border-2 border-amber-200 bg-white py-3 pl-12 pr-4 text-lg outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-700 hover:text-amber-800"
          >
            ✕
          </button>
        )}
      </div>

      {query.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-2xl border border-amber-200 bg-white shadow-lg">
          {loading && results.length === 0 ? (
            <div className="px-4 py-6 text-center text-amber-700">搜尋中…</div>
          ) : results.length > 0 ? (
            results.map((w, idx) => (
              <Link
                key={w.slug}
                href={`/words/${w.slug}`}
                className="flex items-center justify-between border-b border-amber-50 px-4 py-3 transition hover:bg-amber-50"
                onClick={() => {
                  trackSearchResultClick(w.word, query, idx + 1);
                  handleClear();
                }}
              >
                <div>
                  <span className="font-bold text-amber-900">{w.word}</span>
                  <span className="ml-2 text-sm text-amber-800">
                    {w.meanings.slice(0, 2).join("、")}
                  </span>
                </div>
                <span className="text-xs text-amber-700">{w.level}</span>
              </Link>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-amber-800">
              找不到「{query}」相關的單字
            </div>
          )}
        </div>
      )}
    </div>
  );
}
